import dayjs from 'dayjs';
import type { Tokens, User } from '@/types';

const STORAGE_KEY = 'korsmet-auth-state';
const MAX_CACHE_DAYS = 7;

export interface PersistedAccount {
  tokens: Tokens;
  user?: User;
  storedAt: string;
  /**
   * 会话已经彻底失效（refresh token 也换不动了）。
   *
   * 不把这种账号从列表里删掉 —— 用户仍然认得它，只是需要重新输密码。
   * 和 Google 一样：账号还在，标成「已退出」，点一下去输密码。
   */
  expired?: boolean;
}

/** 旧代码按单账号的形状读写，保留这个别名免得四处改类型。 */
export type PersistedAuthState = PersistedAccount;

interface PersistedAuthStateV2 {
  accounts: PersistedAccount[];
  activeUserId: number | null;
}

const EMPTY: PersistedAuthStateV2 = { accounts: [], activeUserId: null };

const isFresh = (storedAt: string) => dayjs().diff(dayjs(storedAt), 'day') < MAX_CACHE_DAYS;

const isUsable = (account: PersistedAccount | undefined | null): account is PersistedAccount =>
  Boolean(account?.tokens?.accessToken && account.storedAt && isFresh(account.storedAt));

const accountId = (account: PersistedAccount) => account.user?.id ?? null;

const writeRaw = (state: PersistedAuthStateV2) => {
  if (typeof window === 'undefined') {
    return;
  }
  if (state.accounts.length === 0) {
    window.localStorage.removeItem(STORAGE_KEY);
    return;
  }
  window.localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
};

/**
 * 读出全部账号。
 *
 * 同时吃两种结构：v1 是单个 { tokens, user, storedAt } 对象，v2 是 { accounts, activeUserId }。
 * 不兼容 v1 的话，这次发版会把所有已登录用户踢下线。
 */
const readRaw = (): PersistedAuthStateV2 => {
  if (typeof window === 'undefined') {
    return EMPTY;
  }
  const raw = window.localStorage.getItem(STORAGE_KEY);
  if (!raw) {
    return EMPTY;
  }

  try {
    const parsed = JSON.parse(raw) as PersistedAuthStateV2 | PersistedAccount;

    const accounts = Array.isArray((parsed as PersistedAuthStateV2).accounts)
      ? (parsed as PersistedAuthStateV2).accounts
      : [parsed as PersistedAccount];

    const usable = accounts.filter(isUsable);
    if (usable.length === 0) {
      window.localStorage.removeItem(STORAGE_KEY);
      return EMPTY;
    }

    const wantedActive = (parsed as PersistedAuthStateV2).activeUserId ?? accountId(usable[0]);
    const active = usable.some((item) => accountId(item) === wantedActive)
      ? wantedActive
      : accountId(usable[0]);

    return { accounts: usable, activeUserId: active };
  } catch (_error) {
    window.localStorage.removeItem(STORAGE_KEY);
    return EMPTY;
  }
};

export const loadAccounts = (): PersistedAccount[] => readRaw().accounts;

export const loadActiveUserId = (): number | null => readRaw().activeUserId;

/** 当前生效的账号。没有多账号时和以前的行为一模一样。 */
export const loadAuthState = (): PersistedAccount | null => {
  const { accounts, activeUserId } = readRaw();
  return accounts.find((item) => accountId(item) === activeUserId) ?? accounts[0] ?? null;
};

/**
 * 写入一个账号并把它设为当前账号。同一个用户重复登录是覆盖，不会堆出两条。
 */
export const persistAuthState = (payload: Omit<PersistedAccount, 'storedAt'>) => {
  const { accounts, activeUserId } = readRaw();
  // 整条覆盖，expired 标记也一并清掉：能写进来就说明这次拿到了有效令牌。
  const next: PersistedAccount = { ...payload, storedAt: dayjs().toISOString(), expired: false };
  const id = payload.user?.id ?? activeUserId;

  const rest = accounts.filter((item) => accountId(item) !== id);
  writeRaw({ accounts: [...rest, next], activeUserId: id ?? null });
};

/** 标记某个账号的会话已失效。保留在列表里，只是不能直接用了。 */
export const markAccountExpired = (userId: number) => {
  const state = readRaw();
  writeRaw({
    ...state,
    accounts: state.accounts.map((item) => (
      accountId(item) === userId ? { ...item, expired: true } : item
    )),
  });
};

export const setActiveAccount = (userId: number) => {
  const state = readRaw();
  if (!state.accounts.some((item) => accountId(item) === userId)) {
    return;
  }
  writeRaw({ ...state, activeUserId: userId });
};

/** 退出单个账号，返回接替的那个（没有就是 null）——和 Google 一样，其他账号还在。 */
export const removeAccount = (userId: number | null): PersistedAccount | null => {
  const { accounts, activeUserId } = readRaw();
  const rest = accounts.filter((item) => accountId(item) !== userId);

  if (rest.length === 0) {
    writeRaw(EMPTY);
    return null;
  }

  const nextActive = activeUserId === userId ? accountId(rest[rest.length - 1]) : activeUserId;
  writeRaw({ accounts: rest, activeUserId: nextActive });
  return rest.find((item) => accountId(item) === nextActive) ?? rest[rest.length - 1];
};

/**
 * 判断 access token 是否已过期（或即将过期）。
 *
 * 只读 JWT 的 exp，不验签 —— 验签是后端的事，这里只是为了在发请求之前
 * 就知道「这张票已经没用了，先去换一张」，省掉一次必然 401 的往返。
 * 留 30 秒余量给网络往返和前后端的时钟偏差。
 */
export const isTokenExpired = (token: string | undefined | null, skewSeconds = 30) => {
  if (!token) {
    return true;
  }
  const payload = token.split('.')[1];
  if (!payload) {
    return true;
  }
  try {
    const base64 = payload.replace(/-/g, '+').replace(/_/g, '/');
    const bytes = Uint8Array.from(atob(base64), (char) => char.charCodeAt(0));
    const claims = JSON.parse(new TextDecoder().decode(bytes)) as { exp?: number };
    if (!claims.exp) {
      // 没有 exp 就当它有效，别把无法判断的情况一律判死。
      return false;
    }
    return claims.exp * 1000 <= Date.now() + skewSeconds * 1000;
  } catch (_error) {
    return true;
  }
};

/** 清掉全部账号。 */
export const clearAuthState = () => writeRaw(EMPTY);
